import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

import { RootState } from '../store';
// Imported from the slice module directly rather than through the
// '../modules/auth' barrel. Same two bindings, zero behaviour change — but the
// barrel also re-exports the auth PAGES, so every consumer of this module (a
// unit test of the wrapper above all) used to drag the whole auth UI tree,
// its assets and react-hook-form in behind it, and it closed a needless
// import cycle through the services barrel.
import { logout, setCredentials } from '../modules/auth/auth-slice';
import { Login, Response } from '../models';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_BACKEND_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      return headers;
    }
  },
});

/**
 * Exported ONLY so it can be unit-tested with a mocked inner base query; the
 * app still consumes it exclusively through `apiSlice` below.
 *
 * This layer deliberately inspects NOTHING in response bodies beyond the 401
 * refresh dance — admin-portal has no global toast/notifier, and every call
 * site owns its own reporting (see src/utils/api-response.ts). Keep it that
 * way: adding body inspection here would create a second, invisible reporting
 * path that no page can suppress.
 */
export const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401) {
    // send refresh token to get new access token
    const refreshResult = await baseQuery(
      {
        url: '/crossbuildz/authenticate/refreshToken',
        method: 'POST',
        body: { refreshToken: (api.getState() as RootState).auth.refreshToken },
      },
      api,
      extraOptions
    );

    // OPTIONAL-CHAINED, and the cast now admits `undefined`. A refresh that
    // fails at the transport level — a non-2xx, a dropped connection, a parse
    // error: precisely the conditions under which a session expires — resolves
    // to `{ error }` with NO `data`, and the previous unguarded
    // `(refreshResult?.data as Response<Login>).data` threw a TypeError right
    // here, INSIDE the base query. That throw skipped the logout branch below,
    // so the expired tokens stayed in localStorage and the user was left on a
    // dead session with no way back to the login screen.
    const refreshData = (refreshResult?.data as Response<Login> | undefined)
      ?.data;

    // BOTH halves are required, not just a non-null object. An optional chain
    // alone still lets a half-built refresh body through, and the persist
    // branch would then write the literal string 'undefined' into localStorage
    // — the same garbage-token hole the login page closes. A half-built
    // session is a FAILED refresh; fail closed to logout, which is always
    // recoverable by signing in again.
    if (refreshData?.accessToken && refreshData?.refreshToken) {
      // Display-only, and never allowed to persist as the string 'undefined'.
      const userName = refreshData.userName || '';

      // store the new token
      api.dispatch(
        setCredentials({
          accessToken: refreshData.accessToken,
          refreshToken: refreshData.refreshToken,
          userName,
        })
      );

      localStorage.setItem('accessToken', refreshData.accessToken);
      localStorage.setItem('refreshToken', refreshData.refreshToken);
      localStorage.setItem('userName', userName);
      // retry the original query with new access token
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logout());

      ['accessToken', 'refreshToken', 'userName'].forEach(key =>
        localStorage.removeItem(key)
      );
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReAuth,
  endpoints: () => ({}),
  tagTypes: ['Company'],
});
